// src/pages/AddReadingQuestionsToIELTS.jsx
import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowRight,
  Plus,
  Trash2,
  Save,
  CheckCircle,
  Image as ImageIcon,
  X,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Highlighter,
} from "lucide-react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import { ieltsQuestionsAPI } from "../../services/ieltsService";
import TranslationField from "../../components/TranslationField";

/* ------------------------------------------------------------------ */
/* Rich text editor for the question text (Bold / Italic / Underline / Highlight) */
/* ------------------------------------------------------------------ */
function ToolbarBtn({ active, onClick, children, title }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`p-1.5 rounded-md transition-colors ${
        active
          ? "bg-orange-100 text-orange-700"
          : "text-gray-500 hover:bg-gray-100"
      }`}
    >
      {children}
    </button>
  );
}

function QuestionTextEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Highlight.configure({ multicolor: false }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none min-h-[70px] px-3 py-2 focus:outline-none",
      },
    },
  });

  if (!editor) return null;

  return (
    <div className="border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-orange-500">
      <div className="flex gap-1 border-b border-gray-200 p-1 bg-gray-50 rounded-t-lg">
        <ToolbarBtn
          title="Bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Underline"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Highlight"
          active={editor.isActive("highlight")}
          onClick={() => editor.chain().focus().toggleHighlight().run()}
        >
          <Highlighter className="w-4 h-4" />
        </ToolbarBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

/* ------------------------------------------------------------------ */

function QuestionForm({ index, onSave, onCancel, passageId, saved }) {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    question_text: "",
    explanation: "",
    english_explanation: "",
    translation: "",
    points: 1,
    is_active: true,
  });
  const [options, setOptions] = useState(["", "", "", ""]);
  const [correctIndex, setCorrectIndex] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const handleQuestionTextChange = (html) => {
    setForm((prev) => ({ ...prev, question_text: html }));
  };

  const handleOptionChange = (idx, val) => {
    setOptions((prev) => prev.map((o, i) => (i === idx ? val : o)));
  };

  const removeOption = (idx) => {
    if (options.length <= 2) return;
    setOptions((prev) => prev.filter((_, i) => i !== idx));
    if (correctIndex === idx) setCorrectIndex(null);
    else if (correctIndex > idx) setCorrectIndex((c) => c - 1);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSave = async () => {
    if (correctIndex === null) {
      setErrors({ correct_answer: "The correct answer must be selected." });
      return;
    }
    // question_text now comes from the rich text editor as HTML,
    // so an empty value looks like "<p></p>" instead of ""
    const isQuestionTextEmpty =
      !form.question_text ||
      form.question_text.replace(/<[^>]*>/g, "").trim() === "";
    if (isQuestionTextEmpty) {
      setErrors({ question_text: "Question text is required." });
      return;
    }
    setLoading(true);
    setErrors({});
    try {
      const formData = new FormData();
      formData.append("question_text", form.question_text);
      formData.append("explanation", form.explanation);
      formData.append("english_explanation", form.english_explanation);
      formData.append("translation", form.translation || "");
      formData.append("points", form.points);
      formData.append("is_active", form.is_active ? "True" : "False");
      formData.append("correct_answer", options[correctIndex]);
      formData.append("options", JSON.stringify(options.filter((o) => o.trim())));
      if (imageFile) {
        formData.append("question_image", imageFile);
      }

      await ieltsQuestionsAPI.createReadingQuestion(passageId, formData);
      onSave();
    } catch (err) {
      setErrors(err.response?.data || { general: "error occurred" });
    } finally {
      setLoading(false);
    }
  };

  if (saved) {
    return (
      <div className="border border-green-200 bg-green-50 rounded-xl p-4 flex items-center gap-3">
        <CheckCircle className="w-5 h-5 text-green-600" />
        <p className="text-green-700 font-medium text-sm">
          Question {index + 1} has been saved successfully.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-xl p-4 space-y-4">
      <p className="font-semibold text-gray-700 text-sm">
        question {index + 1}
      </p>

      <div>
        <QuestionTextEditor
          value={form.question_text}
          onChange={handleQuestionTextChange}
        />
        {errors.question_text && (
          <p className="text-red-500 text-xs mt-1">{errors.question_text}</p>
        )}
        <TranslationField
          label="ترجمة نص السؤال"
          value={form.translation}
          onChange={(v) => setForm((prev) => ({ ...prev, translation: v }))}
        />
      </div>

      {/* Question Image */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Question Image (Optional)
        </label>
        {imagePreview ? (
          <div className="relative inline-block">
            <img
              src={imagePreview}
              alt="Preview"
              className="w-full max-h-40 object-contain rounded-lg border border-gray-300"
            />
            <button
              type="button"
              onClick={removeImage}
              className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg py-4 cursor-pointer hover:border-orange-400 transition-colors">
            <ImageIcon className="w-6 h-6 text-gray-400 mb-1" />
            <span className="text-xs text-gray-500">Click to upload an image</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
            />
          </label>
        )}
      </div>

      <div className="space-y-2">
        {options.map((opt, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCorrectIndex(idx)}
              className={`w-5 h-5 rounded-full border-2 shrink-0 transition-colors ${
                correctIndex === idx
                  ? "bg-green-500 border-green-500"
                  : "border-gray-300"
              }`}
            />
            <input
              type="text"
              value={opt}
              onChange={(e) => handleOptionChange(idx, e.target.value)}
              placeholder={`choice ${String.fromCharCode(65 + idx)}`}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
            />
            <button
              type="button"
              onClick={() => removeOption(idx)}
              className="text-red-400 hover:text-red-600"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {errors.correct_answer && (
          <p className="text-red-500 text-xs">{errors.correct_answer}</p>
        )}
      </div>

      <input
        type="text"
        value={form.explanation}
        onChange={(e) =>
          setForm((f) => ({ ...f, explanation: e.target.value }))
        }
        placeholder="explanation (Optional)"
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
      />
      <input
        type="text"
        value={form.english_explanation}
        onChange={(e) =>
          setForm((f) => ({ ...f, english_explanation: e.target.value }))
        }
        placeholder="English explanation (Optional)"
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
      />

      {errors.general && (
        <p className="text-red-500 text-xs">{errors.general}</p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={loading}
          className="flex items-center gap-1 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white px-4 py-2 rounded-lg text-sm"
        >
          <Save className="w-3.5 h-3.5" />
          {loading ? "saving.." : "save"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function AddReadingQuestionsToIELTS() {
  const { skillId, passageId } = useParams();
  const navigate = useNavigate();
  const [questionForms, setQuestionForms] = useState([{ id: 0, saved: false }]);
  const [nextId, setNextId] = useState(1);

  const addForm = () => {
    setQuestionForms((prev) => [...prev, { id: nextId, saved: false }]);
    setNextId((n) => n + 1);
  };

  const markSaved = (id) => {
    setQuestionForms((prev) =>
      prev.map((f) => (f.id === id ? { ...f, saved: true } : f))
    );
  };

  const removeForm = (id) => {
    setQuestionForms((prev) => prev.filter((f) => f.id !== id));
  };

  const savedCount = questionForms.filter((f) => f.saved).length;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to={`/dashboard/ielts/skills/${skillId}`}
          className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
        >
          <ArrowRight className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Add reading passage questions
          </h1>
          <p className="text-gray-500 text-sm">
            Add questions to the passage you created
          </p>
        </div>
      </div>

      {savedCount > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-green-700 text-sm">
          ✅ {savedCount} questions have been saved so far{" "}
        </div>
      )}

      <div className="space-y-4">
        {questionForms.map((form, index) => (
          <QuestionForm
            key={form.id}
            index={index}
            passageId={passageId}
            saved={form.saved}
            onSave={() => markSaved(form.id)}
            onCancel={() => removeForm(form.id)}
          />
        ))}
      </div>

      <div className="flex gap-3">
        <button
          onClick={addForm}
          className="flex items-center gap-2 bg-orange-50 hover:bg-orange-100 text-orange-600 px-4 py-2 rounded-lg text-sm border border-orange-200 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Another question
        </button>
        <button
          onClick={() => navigate(`/dashboard/ielts/skills/${skillId}`)}
          className="flex-1 bg-gray-800 hover:bg-gray-900 text-white px-4 py-2 rounded-lg text-sm transition-colors"
        >
          Finished Back to Skill
        </button>
      </div>
    </div>
  );
}